// src/components/vendorChat/VendorChatSidebar.tsx
import { Image, Box, Flex, Grid, GridItem, Icon, Text } from '@chakra-ui/react';
import MessageIcon from '@/assets/svg/message-icon.svg?react';
import { useQueryClient } from 'react-query';
import React from 'react';
import { IChat, IChatListResponse } from '@/services/message/types';

interface Props {
  handleChatItemClick: (chat: IChat) => void;
  selectedChat: IChat | null;
}

const formatDate = (d: string) => {
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const VendorChatSidebar: React.FC<Props> = React.memo(({ handleChatItemClick, selectedChat }) => {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<IChatListResponse>(['vendor-chats', 1, 20]);
  const chatList = data?.results ?? [];

  return (
    <Box>
      <Box px="24px" py="16px" borderBottom="1px" borderColor="border.default60">
        <Flex gap={2} align="center">
          <Icon boxSize="24px">
            <MessageIcon />
          </Icon>
          <Text fontFamily="primary" fontSize="16px" fontWeight="semibold">
            Messages
          </Text>
        </Flex>
      </Box>

      <Box
        height={{ base: '75vh', lg: '500px' }}
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {chatList.map((chat) => {
          const lastMsg = chat.messages?.[chat.messages.length - 1];
          const isUnread = lastMsg?.sender?.user_type === 'user' && lastMsg?.is_read === false;

          return (
            <Box
              key={chat.id}
              border="1px"
              borderColor="border.default"
              borderTop="none"
              borderRight="none"
              borderLeft="none"
              p="16px"
              bg={selectedChat?.id === chat.id ? 'background.tertiary' : 'white'}
              cursor="pointer"
              onClick={() => handleChatItemClick(chat)}
            >
              <Grid templateColumns="repeat(6, 1fr)" gap="16px" alignItems="center">
                <GridItem colSpan={1}>
                  <Box
                    boxSize="36px"
                    borderRadius="full"
                    overflow="hidden"
                    bg={chat.user.photo_small ? 'transparent' : '#71B58E'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="white"
                    fontWeight="semibold"
                    fontFamily="primary"
                    fontSize="12px"
                    lineHeight="1"
                  >
                    {chat.user.photo_small ? (
                      <Image
                        src={chat.user.photo_small}
                        alt={`${chat.user.first_name} ${chat.user.last_name}`}
                        boxSize="100%"
                        objectFit="cover"
                      />
                    ) : (
                      <Text as="span">
                        {(chat.user.first_name?.[0] ?? '') + (chat.user.last_name?.[0] ?? '')}
                      </Text>
                    )}
                  </Box>
                </GridItem>

                <GridItem colSpan={4}>
                  <Flex direction="column">
                    <Text
                      fontFamily="primary"
                      fontWeight="semibold"
                      fontSize="12px"
                      color="text.primary"
                    >
                      {chat.user.first_name} {chat.user.last_name}
                    </Text>
                    <Text
                      fontFamily="primary"
                      fontWeight={isUnread ? 'bold' : 'normal'}
                      fontSize="11px"
                      color={isUnread ? 'text.primary' : 'text.secondary'}
                      noOfLines={1}
                    >
                      {lastMsg?.content || 'No messages'}
                    </Text>
                  </Flex>
                </GridItem>

                <GridItem colSpan={1}>
                  <Flex direction="column" align="end">
                    <Text fontFamily="primary" fontSize="9px" color="text.secondary">
                      {formatDate(chat.updated_at)}
                    </Text>
                  </Flex>
                </GridItem>
              </Grid>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

export default VendorChatSidebar;
